import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CaretRightIcon from "../Icons/CaretRightIcon";
// import GearIcon from "../Icons/GearIcon"; // removed: gear menu replaced by inline Rollup dropdown
import Table, { TableRowData } from "../Table";
import {
  CurrencyIcon,
  DataReliabilityIcon,
  MissionIcon,
  ScheduleIcon,
  SummaryIcon,
} from "../TableIcons";
import { getCompletedIndicator, getKpiIndicator } from "../../utils/helpers";
import { FIELD_TYPES } from "../../lib/constants";
import styles from "./GridTable.module.css";
import { useProjectSelection } from "../../state/ProjectSelectionContext";
// DEV-932 Phase 7 follow-up: was flatfile imports + empty-array stubs.
// Component now reads useApiCommunityData() directly inside the body so
// fallbacks pull live DB data instead of empty stubs. Parents that DO
// pass apiX props still take precedence — same priority order as before,
// just with a real fallback instead of [].
import { useApiCommunityData } from "../../api/useApiCommunityData";

type Props = {
  data: any[];
  // optional 2nd arg: which KPI tab to open on the right
  onSelect: (item: any, tabIndex?: number) => void;

  /**
   * Optional controlled project search text. If provided, the table will use
   * this value for filtering instead of its internal state.
   */
  searchText?: string;
  /** Optional handler for controlled project search text. */
  onSearchTextChange?: (next: string) => void;
  /** Hide the built-in search input (useful when the search UI is rendered elsewhere). */
  hideSearch?: boolean;
  /** Hide the built-in collection dropdown (useful when collection selection is managed externally). */
  hideCollection?: boolean;
  /**
   * DEV-920 Layer 2 follow-up. Externally-controlled collection
   * selection. When provided, the table treats this as the active
   * collection (drives schema_guid lookup → hierarchy filter → Group By
   * options) instead of its own internal `selectedCollectionGuid` state.
   *
   * Required by parents that hide the internal collection picker
   * (`hideCollection={true}`) but still expose their OWN page-level
   * collection filter — without this prop, the parent's filter would
   * change which projects appear in `data` but the table's schema
   * lookup would stay stuck on whatever was last persisted internally
   * (often "__ALL__" / null schema), and Group By would never show
   * the hierarchies declared on the actually-selected collection's
   * schema. PortfolioPage split view is the canonical case.
   *
   * Pass undefined to keep the legacy uncontrolled behavior.
   */
  selectedCollectionGuid?: string;
  /** KPI evaluation mode toggle (Live vs Official). Shown in the settings (gear) menu when provided. */
  kpiMode?: "live" | "official";
  onKpiModeChange?: (mode: "live" | "official") => void;
  /** Enables developer debug panels in the right-side KPI tabs. */
  debugMode?: boolean;
  onDebugModeChange?: (enabled: boolean) => void;

  /** Simulated user context (used by the gear menu user switcher and security-aware UI). */
  currentUserGuid?: string;
  onUserChange?: (userGuid: string) => void;

  /** Collections the current user is authorized to see (filters the Collections dropdown). */
  authorizedCollectionGuids?: string[];

  /** API-sourced collection data. When provided, overrides the static seed imports. */
  apiCollections?: Record<string, any>[];
  apiCollectionProjects?: Record<string, any>[];
  apiSchemaFields?: Record<string, any>[];
  apiMetadataValues?: Record<string, any>[];
  apiCustomEntities?: Record<string, any>[];

  /** External focus (e.g., from KpiNavigation). When provided, the grid will highlight the KPI cell for the selected row. */
  externalFocusKpiField?: string;
  externalFocusRowId?: string;
};

type GroupOption = { value: string; label: string };

// DEV-920 Layer 2. Hierarchy Group By options are now derived from
// schema_hierarchies (per-schema, declared by community admins via the
// Schemas tab editor). The previous hardcoded HIERARCHY_GROUP_OPTIONS
// constant is gone; "community" (Geography) is still always offered as
// the canonical platform-seeded hierarchy and is composed in groupOptions.

const STANDARD_GROUP_OPTIONS: GroupOption[] = [
  { value: "", label: "No group" },
  { value: "project_category", label: "Project Category" },
  { value: "project_reach", label: "Project Reach" },
  { value: "project_status", label: "Project Status" },
  { value: "content_status", label: "Content Status" },
];

const TAB_BY_FIELD: Record<string, number> = {
  [FIELD_TYPES.SUMMARY]: 0,
  [FIELD_TYPES.SCHEDULE]: 1,
  [FIELD_TYPES.FINANCE]: 2,
  [FIELD_TYPES.MISSION]: 3,
  [FIELD_TYPES.DATA]: 4,
};

// Ordered KPI fields used throughout the table/detail views.
const KPI_FIELDS: string[] = [
  FIELD_TYPES.SUMMARY,
  FIELD_TYPES.SCHEDULE,
  FIELD_TYPES.FINANCE,
  FIELD_TYPES.MISSION,
  FIELD_TYPES.DATA,
];

const norm = (v: any) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

const joinSearchParts = (parts: any[]) =>
  parts
    .flatMap((p) => {
      if (p === null || p === undefined) return [];
      if (Array.isArray(p)) return p;
      return [p];
    })
    .map((p) => String(p))
    .filter((s) => !!s && s.trim().length > 0)
    .join(" ")
    .toLowerCase();

type AggregationMethod = "average" | "worst" | "best";
const AGG_METHOD_STORAGE_KEY = "kpi_group_aggregation_method";
const LS_SAVED_VIEWS_KEY = "l4e_kpigrid_saved_views";

/** Change-highlight time window options. "off" disables the pulsing halo. */
type ChangeHighlightWindow = "off" | "15m" | "1h" | "3h" | "1d" | "1w" | "1mo";
const LS_CHANGE_HIGHLIGHT_KEY = "l4e_changeHighlightWindow";
const CHANGE_HIGHLIGHT_OPTIONS: { value: ChangeHighlightWindow; labelKey: string }[] = [
  { value: "off",  labelKey: "grid.changesOff" },
  { value: "15m",  labelKey: "grid.changes15m" },
  { value: "1h",   labelKey: "grid.changes1h" },
  { value: "3h",   labelKey: "grid.changes3h" },
  { value: "1d",   labelKey: "grid.changes1d" },
  { value: "1w",   labelKey: "grid.changes1w" },
  { value: "1mo",  labelKey: "grid.changes1mo" },
];

/** Convert a ChangeHighlightWindow value to milliseconds. Returns 0 for "off". */
function changeWindowToMs(w: ChangeHighlightWindow): number {
  switch (w) {
    case "15m": return 15 * 60 * 1000;
    case "1h":  return 60 * 60 * 1000;
    case "3h":  return 3 * 60 * 60 * 1000;
    case "1d":  return 24 * 60 * 60 * 1000;
    case "1w":  return 7 * 24 * 60 * 60 * 1000;
    case "1mo": return 30 * 24 * 60 * 60 * 1000;
    default:    return 0;
  }
}

// Module-level store for KPI change timestamps.
// Lives outside the component so it survives React key-driven remounts
// (PortfolioPage sets key={`kgt-${dataVersion}-portfolio`} which remounts
// the grid every time projectConfigVersion bumps).
// Keys are "projectGuid::fieldType" so only the affected KPI column pulses.
// When fieldType is omitted (legacy callers), all 5 columns pulse.
// Will be replaced by backend `last_changed_at` timestamps later.
const _kpiChangedAtStore: Record<string, number> = {};

const KpiGridTable: FC<Props> = ({
  data,
  onSelect,
  kpiMode,
  onKpiModeChange,
  debugMode,
  onDebugModeChange,
  currentUserGuid,
  onUserChange,
  authorizedCollectionGuids,
  apiCollections,
  apiCollectionProjects,
  apiSchemaFields,
  apiMetadataValues,
  apiCustomEntities,
  searchText: searchTextProp,
  onSearchTextChange,
  hideSearch,
  hideCollection,
  selectedCollectionGuid: controlledCollectionGuid,
  externalFocusKpiField,
  externalFocusRowId,
}) => {
  const { t } = useAppTranslation('kpi');
  const { selectedProjectGuid } = useProjectSelection();
  const [activeRowInd, setActiveRowInd] = useState<number | null>(null);

  // Change-highlight time window (persisted in localStorage)
  const [changeHighlight, setChangeHighlightState] = useState<ChangeHighlightWindow>(() => {
    try {
      const stored = localStorage.getItem(LS_CHANGE_HIGHLIGHT_KEY);
      if (stored && CHANGE_HIGHLIGHT_OPTIONS.some((o) => o.value === stored)) {
        return stored as ChangeHighlightWindow;
      }
    } catch { /* noop */ }
    return "off";
  });
  const setChangeHighlight = useCallback((v: ChangeHighlightWindow) => {
    setChangeHighlightState(v);
    try { localStorage.setItem(LS_CHANGE_HIGHLIGHT_KEY, v); } catch { /* noop */ }
  }, []);

  // DEV-932 Phase 7 follow-up: hook fallback replaces flatfile fallback.
  // Priority: caller-supplied props → useApiCommunityData → empty.
  //
  // DEV-946: each effective* must be wrapped in useMemo so the conditional
  // produces a stable reference. Without memoization, every parent render
  // creates a new array reference, invalidating downstream memo deps and
  // forcing the entire grid to recompute on every keystroke. Memo deps
  // include both the prop and the hook-source so live API updates flow.
  const _hookCommunityData = useApiCommunityData();
  const effectiveCollections = useMemo(
    () => (apiCollections && apiCollections.length > 0
      ? apiCollections
      : (_hookCommunityData.collections || [])),
    [apiCollections, _hookCommunityData.collections],
  );
  const effectiveCollectionProjects = useMemo(
    () => (apiCollectionProjects && apiCollectionProjects.length > 0
      ? apiCollectionProjects
      : (_hookCommunityData.collectionProjects || [])),
    [apiCollectionProjects, _hookCommunityData.collectionProjects],
  );
  const effectiveSchemaFields = useMemo(
    () => (apiSchemaFields && apiSchemaFields.length > 0
      ? apiSchemaFields
      : (_hookCommunityData.schemaFields || [])),
    [apiSchemaFields, _hookCommunityData.schemaFields],
  );
  const effectiveMetadataValues = useMemo(
    () => (apiMetadataValues && apiMetadataValues.length > 0
      ? apiMetadataValues
      : (_hookCommunityData.metadataValues || [])),
    [apiMetadataValues, _hookCommunityData.metadataValues],
  );
  const effectiveCustomEntities = useMemo(
    () => (apiCustomEntities && apiCustomEntities.length > 0
      ? apiCustomEntities
      : (_hookCommunityData.customEntities || [])),
    [apiCustomEntities, _hookCommunityData.customEntities],
  );
  // DEV-920 Layer 2. Schema hierarchies sourced from the API hook (no prop
  // override yet — no upstream caller needs to inject these). Drives both
  // the Group By dropdown options and the generic N-tier tree builder.
  const effectiveHierarchies = useMemo(
    () => (_hookCommunityData.hierarchies || []),
    [_hookCommunityData.hierarchies],
  );

  // DEV-920 Layer 2 follow-up. The hierarchy walker's tier-label resolver
  // needs to look entity GUIDs up against BOTH entity sources:
  //
  //   - effectiveCustomEntities — API: /customentities/all — returns the
  //     legacy un-prefixed entity_types (rotary_club, rotary_district,
  //     rotary_zone). Skip's old test data lives here.
  //
  //   - effectiveCommunityEntities — API: /communityentities — returns
  //     the newer org_-prefixed types (org_rotary_club,
  //     org_rotary_interact_club, etc.) created via Sub-Communities.
  //     Skip's new test data (Hampton Rotary Club, Winnacunnet Interact)
  //     lives here.
  //
  // Without both sources, Step 3's hierarchy walker resolves tier values
  // for the legacy data but shows raw GUIDs for entities created via
  // Sub-Communities — exactly the bug surfaced by Skip's screenshot.
  // Larger architectural cleanup (consolidating the two entity stores)
  // is a separate ticket; this is the immediate client-side mitigation.
  const effectiveCommunityEntities = useMemo(
    () => (_hookCommunityData.communityEntities || []),
    [_hookCommunityData.communityEntities],
  );

  // DEV-971 fix. The Geography branch of buildHierarchyData reads
  // community_l1/l2/l3 from each project row, but those fields are
  // CLIENT-DERIVED upstream (App.tsx -> rederiveCommunityFields) from
  // primary_location_entity_guid against the community_entity tree. If
  // the tree (communityEntities + communityEntityRelationships) hasn't
  // loaded yet when buildHierarchyData first runs, projects only have
  // L1 ("United States") populated and L2/L3 are empty. Adding these to
  // the callback's deps forces a recompute when the tree lands, so the
  // table rebuilds with full state/city grouping instead of staying
  // stuck on country-only. User-toggling Group By manually was the
  // existing workaround — this dep makes the toggle unnecessary.
  const effectiveCommunityRelationships = useMemo(
    () => (_hookCommunityData.communityRelationships || []),
    [_hookCommunityData.communityRelationships],
  );

  const LS_VIEW_KEY = "l4e_kpigrid_view_state";
  const LS_CUSTOM_META_KEY = "l4e_project_custom_metadata_overrides";
  const EVT_PROJECT_UPDATED = "l4e_project_updated";

  const safeParse = <T,>(raw: string | null, fallback: T): T => {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };

  const loadViewState = () => {
    try {
      const raw = window?.localStorage?.getItem(LS_VIEW_KEY) ?? null;
      const v = safeParse<any>(raw, {});
      return v && typeof v === "object" ? v : {};
    } catch {
      return {};
    }
  };

  const initialView = loadViewState();
  const [selectedViewId, setSelectedViewId] = useState<string>(() =>
    String(initialView.selectedViewId ?? "__NONE__"),
  );

  type SavedView = { id: string; name: string; state: any };
  const loadSavedViews = (): SavedView[] => {
    try {
      const raw = window?.localStorage?.getItem(LS_SAVED_VIEWS_KEY) ?? null;
      const v = safeParse<any>(raw, []);
      return Array.isArray(v) ? (v as SavedView[]) : [];
    } catch {
      return [];
    }
  };
  const [savedViews, setSavedViews] = useState<SavedView[]>(() =>
    loadSavedViews(),
  );

  // Sentinel value used by the "View" dropdown.
  const __NONE__ = "__NONE__";

  useEffect(() => {
    try {
      window?.localStorage?.setItem(
        LS_SAVED_VIEWS_KEY,
        JSON.stringify(savedViews),
      );
    } catch {
      // ignore
    }
  }, [savedViews]);

  const applySavedView = useCallback(
    (viewId: string) => {
      const id = String(viewId || __NONE__);
      if (id === __NONE__) return;
      const sv = savedViews.find((x) => String(x.id) === id);
      if (!sv || !sv.state) return;
      const st: any = sv.state;
      if (typeof st.selectedCollectionGuid === "string")
        setSelectedCollectionGuid(String(st.selectedCollectionGuid));
      if (typeof st.groupByField === "string")
        setGroupByField(String(st.groupByField));
      if (typeof st.searchText === "string")
        setSearchText(String(st.searchText));
    },
    [savedViews],
  );

  const [selectedCollectionGuid, setSelectedCollectionGuid] = useState<string>(
    () => String(initialView.selectedCollectionGuid ?? "__ALL__"),
  );
  const [groupByField, setGroupByField] = useState<string>(() =>
    String(initialView.groupByField ?? ""),
  );
  const [internalSearchText, setInternalSearchText] = useState<string>(() =>
    String(initialView.searchText ?? ""),
  );
  // DEV-966 follow-up. Show/hide completed-project leaves. Default is
  // SHOW (true) — users see their full portfolio by default. Toggling
  // OFF hides any project where project_status === 'completed' from
  // the leaf list before grouping; group rollups recompute over the
  // remaining (in-progress + not-started) projects. Persisted in
  // LS_VIEW_KEY alongside the rest of the view state so the user's
  // preference survives navigation and refresh.
  const [showCompleted, setShowCompleted] = useState<boolean>(() =>
    typeof initialView.showCompleted === "boolean"
      ? initialView.showCompleted
      : true,
  );
  const searchText =
    typeof searchTextProp === "string" ? searchTextProp : internalSearchText;
  const setSearchText = useCallback(
    (next: string) => {
      if (onSearchTextChange) {
        onSearchTextChange(next);
      } else {
        setInternalSearchText(next);
      }
    },
    [onSearchTextChange],
  );
  // Keep a stable ref to the search input so it doesn't lose focus when table state changes.
  // We also keep the column definitions stable while typing.
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // KPI focus/highlight (when a group KPI is clicked, highlight the group + its project rows)
  const suppressExternalSyncRef = useRef(false);
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);
  const [focusedGroupIds, setFocusedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [focusedKpiField, setFocusedKpiField] = useState<string | null>(null);

  // Some callers (KPINavigator) only know a project_guid. React-Table rows use an internal
  // `row.id` string, so for external focus we support matching by either row.id OR project_guid.
  const getProjectGuidForRow = useCallback((row: any): string => {
    try {
      const o = row?.original ?? row ?? {};
      return String(
        o?.project_guid ??
          o?.project?.project_guid ??
          o?.guid ??
          o?.project?.guid ??
          "",
      );
    } catch {
      return "";
    }
  }, []);

  // External focus sync (e.g., when the user changes KPI tabs in the right-side navigator).
  // When a group KPI cell is clicked locally, the click handler sets focus state and then
  // calls onSelect(), which changes external props. The effect below would wipe out the
  // group highlight. suppressExternalSyncRef prevents that for one render cycle.
  useEffect(() => {
    if (suppressExternalSyncRef.current) {
      suppressExternalSyncRef.current = false;
      return;
    }

    const ft = String(externalFocusKpiField || "").trim();
    const rid = String(externalFocusRowId || "").trim();
    if (!ft || !rid) return;

    // External focus should highlight a *project row* KPI, not a group row.
    // `rid` is expected to be a project_guid (from KPINavigator).
    setFocusedGroupId(null);
    setFocusedGroupIds(new Set());
    setFocusedRowIds(new Set([rid]));
    setFocusedKpiField(ft);
  }, [externalFocusKpiField, externalFocusRowId]);

  const [focusedRowIds, setFocusedRowIds] = useState<Set<string>>(new Set());

  // Recently updated project rows (for brief highlight when metadata/KPI changes occur).
  const [recentlyUpdated, setRecentlyUpdated] = useState<
    Record<string, number>
  >({});

  // Bump counter that forces columns to re-memoize after a change timestamp
  // is recorded. The actual timestamps live in the module-level
  // _kpiChangedAtStore so they survive component remounts caused by key changes.
  const [pulseVersion, setPulseVersion] = useState(0);

  /**
   * Record that a project's KPI indicator changed.
   * @param projectGuid — the project GUID
   * @param fields — which KPI field(s) changed. When provided, only those
   *   columns will pulse. An event with field "schedule" also auto-includes
   *   "summary" since summary is derived from all sub-KPIs.
   */
  const markRowUpdated = useCallback((projectGuid: string, fields?: string[]) => {
    const g = String(projectGuid || "");
    if (!g) return;
    const now = Date.now();
    // Write to module-level store keyed by "guid::field"
    if (fields && fields.length > 0) {
      for (const f of fields) {
        _kpiChangedAtStore[`${g}::${f}`] = now;
      }
      // Summary is always affected when any sub-KPI changes
      if (!fields.includes("summary")) {
        _kpiChangedAtStore[`${g}::summary`] = now;
      }
    } else {
      // Legacy: no field specified → mark all columns
      _kpiChangedAtStore[`${g}::summary`] = now;
      _kpiChangedAtStore[`${g}::schedule`] = now;
      _kpiChangedAtStore[`${g}::finance`] = now;
      _kpiChangedAtStore[`${g}::mission`] = now;
      _kpiChangedAtStore[`${g}::data`] = now;
    }
    // Bump pulse version so columns re-memoize
    setPulseVersion((v) => v + 1);
    // Brief row flash (2.5s)
    setRecentlyUpdated((prev) => ({ ...prev, [g]: now }));
    window.setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = { ...prev };
        delete next[g];
        return next;
      });
    }, 2500);
  }, []);

  /**
   * Determine whether a specific KPI cell should pulse.
   * @param projectGuid — the project GUID
   * @param field — the KPI field type (e.g. "schedule", "finance")
   */
  const shouldPulseCell = useCallback((projectGuid: string, field: string): boolean => {
    if (changeHighlight === "off") return false;
    const key = `${String(projectGuid || "")}::${field}`;
    const changedAt = _kpiChangedAtStore[key];
    if (!changedAt) return false;
    const windowMs = changeWindowToMs(changeHighlight);
    return (Date.now() - changedAt) <= windowMs;
  }, [changeHighlight, pulseVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic tick so pulse stops when the change window expires.
  const [pulseTick, setPulseTick] = useState(0);
  useEffect(() => {
    if (changeHighlight === "off") return;
    const hasAny = Object.keys(_kpiChangedAtStore).length > 0;
    if (!hasAny) return;
    const id = window.setInterval(() => setPulseTick((v) => v + 1), 30_000);
    return () => window.clearInterval(id);
  }, [changeHighlight, pulseVersion]);  

  // Bump this counter to force recomputation of effective custom metadata from localStorage overrides.
  const [customMetaVersion, setCustomMetaVersion] = useState<number>(0);

  // Task #224. Animation is now data-driven rather than event-driven.
  //
  // Old model (removed): an EVT_PROJECT_UPDATED listener fired
  // markRowUpdated on every save event regardless of whether the row's
  // KPI values actually changed. SignalR echoed the same save back,
  // causing a duplicate fire that visibly restarted the pulse mid-
  // flight. A 1s dedupe was bolted on to mask the second fire.
  //
  // New model: a snapshot diff runs after tableData recomputes (see the
  // useEffect placed below the tableData declaration). For each project
  // leaf, we compare prior `{ sortIndex, color/trend per KPI }` against
  // new. We animate ONLY when a row's KPI value truly changed, OR when
  // its sorted position moved (e.g., because a grouping field changed).
  // Pure echo events are idempotent — the snapshot's already current —
  // so no dedupe machinery is needed.
  //
  // Cell pulse (kpi-pulse-wrapper) fires only for the SPECIFIC fields
  // whose value changed. Row flash fires when ANY field changed OR the
  // sort position moved.
  //
  // Type + ref live up here so the effect (declared further down where
  // tableData is in scope) can populate them without forward-reference
  // issues.
  type KpiSnapshot = {
    sortIndex: number;
    schedule: { color: number; trend: number };
    finance: { color: number; trend: number };
    mission: { color: number; trend: number };
    data: { color: number; trend: number };
    summary: { color: number; trend: number };
  };
  const priorSnapshotRef = useRef<Record<string, KpiSnapshot> | null>(null);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e) return;
      if (e.key === LS_CUSTOM_META_KEY) {
        setCustomMetaVersion((v) => v + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);


  const clearFocus = useCallback(() => {
    setFocusedGroupId(null);
    setFocusedGroupIds(new Set());
    setFocusedKpiField(null);
    setFocusedRowIds(new Set());
  }, []);
  const collectDescendantIds = useCallback(
    (row: any) => {
      // Group rows should use their stable group id (row.original.id).
      // Leaf rows should use *project_guid* so focus/highlight survives regrouping/sorting.
      const stableGroupId = (r: any) =>
        String((r?.original as any)?.id ?? r?.id ?? "");

      const groupIds = new Set<string>();
      const leafIds = new Set<string>();

      // Check if this is a "group of groups" — immediate children contain at least one group.
      const children = (row.subRows || []) as any[];
      const hasChildGroups = children.some(
        (c: any) => (c.original as any)?.__type === "group",
      );

      if (hasChildGroups) {
        // Group-of-groups: only highlight the immediate child groups (subgroups),
        // NOT the projects within them. This keeps the highlight scoped to
        // the direct subgroups level.
        for (const c of children) {
          if (!c) continue;
          const isGroup = (c.original as any)?.__type === "group";
          if (isGroup) {
            const id = stableGroupId(c);
            if (id) groupIds.add(id);
          }
          // Skip leaf projects — they belong to the subgroups, not this level.
        }
      } else {
        // Regular group: highlight all child projects.
        const visit = (r: any) => {
          if (!r) return;
          const isGroup = (r.original as any)?.__type === "group";
          if (isGroup) {
            const id = stableGroupId(r);
            if (id) groupIds.add(id);
          } else {
            const guid = getProjectGuidForRow(r);
            const id = guid || stableGroupId(r);
            if (id) leafIds.add(id);
          }
          const ch = (r.subRows || []) as any[];
          for (const c of ch) visit(c);
        };
        visit(row);
      }

      return { groupIds, leafIds };
    },
    [getProjectGuidForRow],
  );

  // Group-row KPI aggregation method
  const [aggMethod, setAggMethod] = useState<AggregationMethod>(() => {
    try {
      const v = window?.localStorage?.getItem(AGG_METHOD_STORAGE_KEY);
      return v === "worst" || v === "average" || v === "best" ? v : "average";
    } catch {
      return "average";
    }
  });
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState<number>(0);

  // Keep sticky header offset in sync with the toolbar height.
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;

    const update = () =>
      setToolbarHeight(el.getBoundingClientRect().height || 0);
    update();

    // Prefer ResizeObserver when available for accurate updates (wrap, font changes, etc.)
    const RO = (window as any).ResizeObserver as any;
    if (RO) {
      const ro = new RO(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    // Fallback: update on window resize
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    try {
      window?.localStorage?.setItem(AGG_METHOD_STORAGE_KEY, aggMethod);
    } catch {
      // ignore
    }
  }, [aggMethod]);

  // Persist view state so Fast Refresh/hot reload does not reset the user's view.
  useEffect(() => {
    try {
      const next = { selectedCollectionGuid, groupByField, searchText, showCompleted };
      window?.localStorage?.setItem(LS_VIEW_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }, [selectedViewId, selectedCollectionGuid, groupByField, searchText, showCompleted]);

  const collectionOptions = useMemo(() => {
    const allowed = Array.isArray(authorizedCollectionGuids)
      ? new Set((authorizedCollectionGuids as any[]).map((g) => String(g)))
      : null;

    const base = (effectiveCollections || [])
      .filter((c: any) => c?.is_active !== false)
      .filter((c: any) => !allowed || allowed.has(String(c.collection_guid)))
      .map((c: any) => ({
        value: c.collection_guid,
        label: c.collection_name,
        schema_guid: c.schema_guid,
      }));

    // Special "no collection" view: uses projects.ts directly and has no custom metadata.
    return [
      { value: "__ALL__", label: "All My Projects", schema_guid: null as any },
      ...base,
    ];
  }, [authorizedCollectionGuids, effectiveCollections]);

  // Validate persisted selectedCollectionGuid against available options.
  // If it points to a stale/removed collection (or a fresh browser with no
  // matching collections loaded yet), fall back to "__ALL__" so the grid
  // doesn't silently filter out every row.
  useEffect(() => {
    if (
      selectedCollectionGuid &&
      selectedCollectionGuid !== "__ALL__" &&
      collectionOptions.length > 0 &&
      !collectionOptions.some((c) => c.value === selectedCollectionGuid)
    ) {
      console.warn('[KpiGridTable] Resetting stale selectedCollectionGuid', selectedCollectionGuid, '→ __ALL__');
      setSelectedCollectionGuid("__ALL__");
    }
  }, [collectionOptions, selectedCollectionGuid]);

  /**
   * DEV-920 Layer 2 follow-up. Effective collection GUID for schema +
   * membership lookups. Prefers the externally-controlled prop when
   * provided (parent's page-level filter), falls back to the internal
   * state (uncontrolled / built-in dropdown) otherwise. Without this
   * indirection, parents that hide the internal dropdown and drive
   * selection from a page-level filter would have their selection
   * silently ignored by the schema lookup, and Group By would never
   * surface hierarchies declared on the actually-selected collection's
   * schema. PortfolioPage split view is the canonical case.
   */
  const effectiveSelectedCollectionGuid = useMemo(() => {
    if (controlledCollectionGuid !== undefined && controlledCollectionGuid !== null) {
      return String(controlledCollectionGuid);
    }
    return selectedCollectionGuid;
  }, [controlledCollectionGuid, selectedCollectionGuid]);

  const selectedCollection = useMemo(() => {
    return (
      collectionOptions.find((c) => c.value === effectiveSelectedCollectionGuid) || null
    );
  }, [collectionOptions, effectiveSelectedCollectionGuid]);

  const membershipSet = useMemo(() => {
    if (!effectiveSelectedCollectionGuid || effectiveSelectedCollectionGuid === "__ALL__")
      return null;
    const set = new Set<string>();
    // DEV-1096. Polymorphic collection_projects rows: each row carries
    // either project_guid OR mini_project_guid depending on member_type.
    // The grid's downstream consumer checks `membershipSet.has(row.project_guid)`
    // against the per-row identifier; for MP rows that identifier is the
    // mini_project_guid. Both fields here are already normalized to upper
    // case via collectionProjectToSeedShape → normalizeGuid, so we add
    // them as-is and let the downstream comparison stay verbatim.
    (effectiveCollectionProjects || [])
      .filter((cp: any) => cp.collection_guid === effectiveSelectedCollectionGuid)
      .forEach((cp: any) => {
        const memberType = String(cp.member_type ?? "project");
        if (memberType === "mini_project" && cp.mini_project_guid) {
          set.add(cp.mini_project_guid);
        } else if (cp.project_guid) {
          set.add(cp.project_guid);
        }
      });
    return set;
  }, [effectiveSelectedCollectionGuid, effectiveCollectionProjects]);

  const entityNameByGuid = useMemo(() => {
    const m = new Map<string, string>();
    (effectiveCustomEntities || []).forEach((e: any) => {
      if (e?.entity_guid) m.set(e.entity_guid, e.name || e.entity_guid);
    });
    return m;
  }, [effectiveCustomEntities]);

  const entityCodeByGuid = useMemo(() => {
    const m = new Map<string, string>();
    (effectiveCustomEntities || []).forEach((e: any) => {
      const g = e?.entity_guid;
      const c = e?.entity_code;
      if (g && c) m.set(String(g), String(c));
    });
    return m;
  }, [effectiveCustomEntities]);

  const schemaFieldByGuid = useMemo(() => {
    const m = new Map<string, any>();
    const schemaGuid = selectedCollection?.schema_guid;
    (effectiveSchemaFields || [])
      .filter((f) => !schemaGuid || f.schema_guid === schemaGuid)
      .forEach((f) => m.set(f.field_guid, f));
    return m;
    // DEV-919 followup: effectiveSchemaFields must be in deps. The list
    // arrives asynchronously via the API hook; without it here, the map
    // is built once from the (initially empty/static) source and never
    // updates when API rows land — schema-field lookups silently miss.
  }, [selectedCollection, effectiveSchemaFields]);

  const groupOptions = useMemo(() => {
    const schemaGuid = selectedCollection?.schema_guid || null;

    const schemaFields = schemaGuid
      ? (effectiveSchemaFields || []).filter(
          (f: any) => f.schema_guid === schemaGuid && !!f.groupable,
        )
      : [];

    // DEV-920 Layer 2. Hierarchies declared on this schema, sourced from
    // schema_hierarchies. Each becomes a `hier:<hierarchyGuid>` Group By
    // option dispatched by the generic N-tier tree builder. "community"
    // (Geography) remains the canonical platform-seeded option and is
    // always offered. Hierarchies are sorted alphabetically by displayName.
    const schemaHierarchies = schemaGuid
      ? (effectiveHierarchies || []).filter(
          (h: any) =>
            String(h.schemaGuid || "").toLowerCase() ===
            String(schemaGuid).toLowerCase(),
        )
      : [];


    // Collect every field GUID participating in any hierarchy on this
    // schema. These are the "tier primitives" that must be excluded from
    // the Collection Fields list — otherwise the dropdown would offer
    // both "Group by Rotary Zone" (raw field) and "Group by Rotary
    // Structure" (the hierarchy), which is redundant and confusing UX.
    const tierFieldGuids = new Set<string>();
    for (const h of schemaHierarchies) {
      try {
        const order = JSON.parse(h.levelOrder);
        if (Array.isArray(order)) {
          for (const guid of order) {
            tierFieldGuids.add(String(guid).toLowerCase());
          }
        }
      } catch {
        // Server validates levelOrder shape on save; if a malformed value
        // somehow reached the client, skip rather than crash. Worst case:
        // a tier field shows up redundantly in Collection Fields.
      }
    }

    const hierarchy: GroupOption[] = [
      { value: "community", label: "Geography" },
      ...schemaHierarchies
        .slice()
        .sort((a: any, b: any) =>
          String(a.displayName || "").localeCompare(String(b.displayName || "")),
        )
        .map((h: any) => ({
          value: `hier:${h.hierarchyGuid}`,
          label: String(h.displayName || h.hierarchyKey || "Hierarchy"),
        })),
    ];

    const collectionFields: GroupOption[] = schemaFields
      .filter(
        (f: any) =>
          !tierFieldGuids.has(String(f.field_guid || "").toLowerCase()),
      )
      .sort((a, b) => String(a.label).localeCompare(String(b.label)))
      .map((f) => ({ value: `custom:${f.field_key}`, label: f.label }));

    return {
      hierarchy,
      standard: STANDARD_GROUP_OPTIONS,
      collection: collectionFields,
    };
    // DEV-919 followup: effectiveSchemaFields belongs in deps. Without
    // it, the hierarchy options and the Collection Fields optgroup are
    // computed once from whatever data is in scope at first render
    // (often empty before the API responds) and stay empty even after
    // fields land. DEV-920 Layer 2 adds effectiveHierarchies for the
    // same reason.
  }, [
    selectedCollection,
    effectiveSchemaFields,
    effectiveHierarchies,
  ]);

  // Join custom metadata values into each project row (per selected collection/schema).
  const collectionScopedData = useMemo(() => {
    const rows = (data || []) as any[];
    const schemaGuid = selectedCollection?.schema_guid;

    // Apply membership filter if we have it; otherwise show all rows (useful for "All projects" later).
    const scoped = membershipSet
      ? rows.filter((it) => membershipSet.has(it?.project_guid))
      : rows;

    if (rows.length > 0 && scoped.length === 0 && membershipSet) {
      console.warn('[KpiGridTable] All rows filtered out by membershipSet!',
        { effectiveSelectedCollectionGuid, membershipSetSize: membershipSet.size, inputRows: rows.length });
    }

    if (!schemaGuid) return scoped;

    const loadOverrides = (): Record<string, any> => {
      try {
        const raw = window?.localStorage?.getItem(LS_CUSTOM_META_KEY) ?? null;
        const parsed = safeParse<Record<string, any>>(raw, {} as any);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    };

    const allOverrides = loadOverrides();

    // Build values index for quick join: owner_guid -> (field_key -> value)
    // Task #215. The metadata value table is polymorphic: rows carry EITHER
    // project_guid (Full Project) OR mini_project_guid (Mini-Project). MP rows
    // in this grid land with `project_guid = mp.miniProjectGuid` (App.tsx
    // portfolio aggregator), so we key the join by whichever owner GUID the
    // value row carries — that matches the row's project_guid field downstream
    // at the lookup site (`byProject.get(it.project_guid)`).
    const byProject = new Map<string, Record<string, any>>();
    (effectiveMetadataValues || [])
      .filter((v: any) => v.schema_guid === schemaGuid)
      .forEach((v) => {
        // MP rows arrive with project_guid=null; fall back to mini_project_guid.
        // Both are normalized upstream so case-insensitive matching isn't required.
        const pid = v.project_guid || v.mini_project_guid;
        if (!pid) return;
        if (!byProject.has(pid)) byProject.set(pid, {});
        const rec = byProject.get(pid)!;

        const field = schemaFieldByGuid.get(v.field_guid);
        if (!field?.field_key) return;

        let value: any = undefined;

        if (field.type === "guid") {
          const g = v.value_guid || v.value_enum;
          value = g ? entityNameByGuid.get(g) || g : undefined;
        } else if (field.type === "number") {
          value =
            typeof v.value_number === "number" ? v.value_number : undefined;
        } else if (field.type === "boolean") {
          value =
            typeof v.value_boolean === "boolean" ? v.value_boolean : undefined;
        } else if (field.type === "date") {
          value = v.value_date || undefined;
        } else {
          value = v.value_string ?? v.value_enum ?? undefined;
        }

        rec[field.field_key] = value ?? rec[field.field_key];
      });

    // Build list of custom fields that should be searchable for this schema.
    // Notes:
    // - default searchable = true
    // - treat string "false" as false (common when hand-editing TS data)
    // - match field keys case-insensitively
    const searchableFieldKeys = new Set<string>();
    (effectiveSchemaFields || [])
      .filter((f: any) => f.schema_guid === schemaGuid)
      .forEach((f: any) => {
        const flag = (f as any).searchable;
        const isSearchable =
          flag !== false && String(flag).toLowerCase() !== "false";
        if (!isSearchable) return;
        if (!f?.field_key) return;
        searchableFieldKeys.add(String(f.field_key).toLowerCase());
      });

    // Helper to interpret a FieldValue override into the resolved display value
    // that the grid expects (string/number/boolean/date). Overrides are stored
    // as field_guid -> FieldValue in localStorage.
    const resolveOverrideValue = (field: any, fv: any) => {
      if (!field || !fv) return undefined;
      const t = String(field.type || "").toLowerCase();

      if (t === "guid") {
        const g = (fv as any).value_guid || (fv as any).value_enum;
        return g ? entityNameByGuid.get(g) || g : undefined;
      }
      if (t === "number") {
        return typeof (fv as any).value_number === "number"
          ? (fv as any).value_number
          : undefined;
      }
      if (t === "boolean") {
        return typeof (fv as any).value_boolean === "boolean"
          ? (fv as any).value_boolean
          : undefined;
      }
      if (t === "date") {
        return (fv as any).value_date || undefined;
      }

      // string/enum fallbacks
      return (fv as any).value_string ?? (fv as any).value_enum ?? undefined;
    };

    return scoped.map((it) => {
      const pid = it?.project_guid;
      const custom: Record<string, any> = { ...(byProject.get(pid) || {}) };

      // Apply per-project overrides from localStorage, if any.
      // Shape: { [project_guid]: { [field_guid]: FieldValue } }
      const perProjectOverrides =
        pid &&
        (allOverrides as any)[pid] &&
        typeof (allOverrides as any)[pid] === "object"
          ? (allOverrides as any)[pid]
          : null;
      if (perProjectOverrides) {
        for (const [fieldGuid, fv] of Object.entries(perProjectOverrides)) {
          const field = schemaFieldByGuid.get(String(fieldGuid));
          if (!field?.field_key) continue;
          const resolved = resolveOverrideValue(field, fv);
          // If override resolves to undefined, treat as cleared.
          if (resolved === undefined) delete custom[field.field_key];
          else custom[field.field_key] = resolved;
        }
      }

      // Base searchable content from the project record.
      const p = it?.project ?? it;
      const baseSearch = joinSearchParts([
        p?.project_name,
        p?.project_status,
        p?.project_reach,
        p?.project_category,
        // legacy + new geography hierarchy
        p?.Community ?? p?.community,
        p?.community_l1,
        p?.community_l2,
        p?.community_l3,
      ]);

      // Add searchable custom fields (resolved display values for guid-type fields).
      const customSearch = joinSearchParts(
        Object.entries(custom)
          .filter(([k]) => searchableFieldKeys.has(String(k).toLowerCase()))
          .flatMap(([k, v]) => {
            // Include key tokens to help searches like "district"/"zone"/"club" match even if the value is numeric.
            const keyTokens = String(k).replace(/[_-]+/g, " ");
            return [keyTokens, v];
          }),
      );

      const __searchText = joinSearchParts([baseSearch, customSearch]);

      return { ...it, custom, __searchText };
    });
    // DEV-946: added effectiveMetadataValues, effectiveSchemaFields, and
    // selectedCollectionGuid. All three were read inside the closure but
    // missing from deps. Without effectiveMetadataValues, custom field
    // values would not appear on the grid until something else re-rendered;
    // without effectiveSchemaFields, the searchable-fields set would be
    // empty until the same trigger; without selectedCollectionGuid, switching
    // collections wouldn't recompute the warning log on the empty-rows path.
  }, [
    data,
    membershipSet,
    selectedCollection,
    schemaFieldByGuid,
    entityNameByGuid,
    effectiveMetadataValues,
    effectiveSchemaFields,
    effectiveSelectedCollectionGuid,
  ]);

  const filteredProjects = useMemo(() => {
    // DEV-966 follow-up. Apply the show/hide completed filter BEFORE
    // the search filter so completed projects are excluded from both
    // the leaf list and any rollup math. Group rollups recompute over
    // the filtered set — a group whose only constituent is a completed
    // project will disappear (zero leaves → bucket dropped).
    const base = showCompleted
      ? (collectionScopedData || [])
      : (collectionScopedData || []).filter((item: any) => {
          const status = String(item?.project?.project_status ?? "").toLowerCase();
          return status !== "completed";
        });

    const raw = (searchText || "").trim().toLowerCase();
    if (!raw) return base as any[];

    // Split into individual terms for AND matching (e.g. "Boston Beach" matches
    // rows containing both "boston" AND "beach" anywhere in their searchable text).
    const terms = raw.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return base as any[];

    return base.filter((item: any) => {
      const hay = String(item?.__searchText ?? "").toLowerCase();
      if (hay) return terms.every((t) => hay.includes(t));

      // Fallback (should rarely be used)
      const p = item?.project ?? item;
      if (!p) return false;
      const fallbackHay = [
        p.project_name,
        p.project_status,
        p.project_reach,
        p.project_category,
        p.Community ?? p.community,
        p.community_l1,
        p.community_l2,
        p.community_l3,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => fallbackHay.includes(t));
    });
  }, [collectionScopedData, searchText, showCompleted]);

  // Used by KPI columns for sorting.
  // Higher color values are "better" (e.g., green > yellow > red) in this prototype.
  // Trend is used as a stable tie-breaker.
  const getKpiSortValue = useCallback((row: any, fieldType: string) => {
    const { colorKey, trendKey, scoreKey } = getKpiValueKeys(fieldType);
    const scoreRaw = row?.[scoreKey];
    const colorRaw = row?.[colorKey];
    const trendRaw = row?.[trendKey];
    const trend = Number.isFinite(Number(trendRaw)) ? Number(trendRaw) : 0;

    // Prefer score_0_100 for higher-resolution sorting; fall back to a
    // color-derived synthetic score so color-only rows sort on the SAME
    // scale as scored rows.
    //
    // Task #223. The original fallback used `color * 100`, which puts
    // color-only rows on a 0-400 numeric range while scored rows live
    // on 0-1000 (`score * 10`). That made Mini-Project rows — which
    // carry color codes but null scores — sort BELOW Full-Project red
    // rows even when the MP was green, because 3*100 = 300 < 40*10 = 400.
    //
    // Mapping each color band to a representative score within its
    // banding lets MPs interleave coherently with FPs at the same color:
    //   grey  (0) → 0    — no data, sorts below everything
    //   red   (1) → 30   — middle of the <60 red band
    //   yellow(2) → 65   — middle of the 60–80 yellow band
    //   green (3) → 85   — middle of the >=80 green band
    // The same `*10 + (trend + 1)` formula then applies, so a green MP
    // (key ~851) lands between a deep-green FP (score 95 → key 951)
    // and a barely-green FP (score 80 → key 801) — exactly where the
    // user expects it.
    const score = scoreRaw !== null && scoreRaw !== undefined && Number.isFinite(Number(scoreRaw))
      ? Number(scoreRaw)
      : null;
    if (score !== null) {
      return score * 10 + (trend + 1);
    }
    const colorCode = Number.isFinite(Number(colorRaw)) ? Number(colorRaw) : 0;
    const colorToRepresentativeScore: Record<number, number> = {
      0: 0,    // grey / no data
      1: 30,   // red (middle of <60 band)
      2: 65,   // yellow (middle of 60–80 band)
      3: 85,   // green (middle of >=80 band)
    };
    const syntheticScore = colorToRepresentativeScore[colorCode] ?? 0;
    return syntheticScore * 10 + (trend + 1);
  }, []);

  const filteredCount = useMemo(
    () => filteredProjects.length,
    [filteredProjects],
  );

  const onRowClickHandler = useCallback(
    (rowData: any) => {
      if (!rowData) return;
      if (
        rowData.__type === "project" &&
        typeof rowData.__flatIndex === "number"
      ) {
        setActiveRowInd(rowData.__flatIndex);
      }
      onSelect?.(rowData.__payload ?? rowData, 0);
    },
    [onSelect],
  );

  const onGroupByChange = useCallback(
    (value: string) => {
      setGroupByField(value);
      setActiveRowInd(null);
      clearFocus();
      onSelect?.(null, 0);
    },
    [onSelect],
  );

  // NOTE: With TanStack v8 we shape hierarchical group rows upstream; no hidden group columns needed.

  const getKpiValueKeys = useCallback((fieldType: string) => {
    if (fieldType === FIELD_TYPES.DATA) {
      return {
        colorKey: "data_reliability_color",
        trendKey: "data_reliability_trend",
        scoreKey: "data_reliability_score_0_100",
      };
    }
    return {
      colorKey: `${fieldType}_indicator_color`,
      trendKey: `${fieldType}_indicator_trend`,
      scoreKey: `${fieldType}_score_0_100`,
    };
  }, []);

  /**
   * Group KPI rollup: aggregates child project KPI values into a single group indicator.
   *
   * Strategy: Use score_0_100 values (continuous 0–100) for aggregation when available,
   * then convert the aggregated score back to a color code using standard banding
   * (≥80 green=3, ≥60 yellow=2, <60 red=1, no-data=0).
   *
   * Null exclusion: Projects with no data (color=0, score=null) are excluded from
   * group rollup calculations so they don't drag down averages.
   */
  const computeGroupKpiValuesFromItems = useCallback(
    (fieldType: string, items: any[]) => {
      if (!items?.length) return { colorValue: 0, trendValue: 0 };

      const { colorKey, trendKey, scoreKey } = getKpiValueKeys(fieldType);

      // Collect scores, color codes, and trends, excluding no-data items (color=0).
      const scores: number[] = [];
      const colorCodes: number[] = [];
      const trendVals: number[] = [];

      for (const it of items) {
        const c = Number((it as any)?.[colorKey]);
        const s = (it as any)?.[scoreKey];
        const t = Number((it as any)?.[trendKey]);

        // Skip no-data items: color value 0 means no data.
        if (Number.isNaN(c) || c === 0) continue;

        // Collect the per-item PP-based color code (1=red, 2=yellow, 3=green).
        colorCodes.push(c);

        // Prefer score_0_100 for score aggregation (more precise than discrete color codes).
        const scoreVal = s !== null && s !== undefined && Number.isFinite(Number(s)) ? Number(s) : null;
        if (scoreVal !== null) {
          scores.push(scoreVal);
        } else {
          // Fallback: derive an approximate score from the color code.
          const fallbackScore =
            (c === 3 || c > 2.5) ? 90   // green
            : (c === 2 || c > 1.5) ? 70 // yellow
            : 40;                         // red
          scores.push(fallbackScore);
        }

        if (!Number.isNaN(t)) trendVals.push(t);
      }

      // If no valid items, return no-data indicator.
      if (scores.length === 0) return { colorValue: 0, trendValue: 0 };

      const safeAvg = (arr: number[]) =>
        arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
      const safeMin = (arr: number[]) => (arr.length ? Math.min(...arr) : 0);
      const safeMax = (arr: number[]) => (arr.length ? Math.max(...arr) : 0);

      // Aggregate scores using the selected method.
      const groupScore =
        aggMethod === "worst"
          ? safeMin(scores)
          : aggMethod === "best"
            ? safeMax(scores)
            : safeAvg(scores);

      // Derive group color from per-item PP-based color codes directly,
      // NOT from the aggregated score. This avoids the mismatch where
      // score-based banding (80/60) disagrees with PP-based color assignment
      // (e.g., behindPP 16-20pp → score <60 maps to red, but PP says yellow).
      let groupColorValue: number;
      if (aggMethod === "worst") {
        // Worst = lowest color code (1=red < 2=yellow < 3=green)
        groupColorValue = safeMin(colorCodes);
      } else if (aggMethod === "best") {
        // Best = highest color code
        groupColorValue = safeMax(colorCodes);
      } else {
        // Average: weighted average of color codes, snapped to discrete.
        const avgColor = safeAvg(colorCodes);
        groupColorValue = avgColor >= 2.5 ? 3 : avgColor >= 1.5 ? 2 : avgColor >= 0.5 ? 1 : 0;
      }

      // Aggregate trend values.
      const trendRaw =
        aggMethod === "worst"
          ? safeMin(trendVals)
          : aggMethod === "best"
            ? safeMax(trendVals)
            : safeAvg(trendVals);

      const eps = 1e-9;
      const groupTrendValue = trendRaw > eps ? 1 : trendRaw < -eps ? -1 : 0;

      return { colorValue: groupColorValue, trendValue: groupTrendValue, scoreValue: Math.round(groupScore) };
    },
    [aggMethod, getKpiValueKeys],
  );

  const computeGroupKpiIconFromItems = useCallback(
    (fieldType: string, items: any[]) => {
      const { colorKey, trendKey } = getKpiValueKeys(fieldType);
      const { colorValue, trendValue } = computeGroupKpiValuesFromItems(
        fieldType,
        items,
      );

      const groupItem: any = {
        project: { project_status: "in progress" },
        [colorKey]: colorValue,
        [trendKey]: trendValue,
      };

      return getKpiIndicator(fieldType, groupItem, "sm");
    },
    [computeGroupKpiValuesFromItems, getKpiValueKeys],
  );

  const buildGroupNode = useCallback(
    (
      label: string,
      keyPath: string[],
      items: any[],
      subRows?: TableRowData[],
    ): TableRowData => {
      const nodeId = ["group", ...keyPath].join("::");
      const depth = Math.max(0, keyPath.length - 1);

      // Compute KPI rollups so group details can render KPI shapes.
      const kpiRollups: Record<string, { color: number; trend: number; score: number }> = {};
      for (const ft of KPI_FIELDS) {
        const { colorValue, trendValue, scoreValue } = computeGroupKpiValuesFromItems(
          ft,
          items,
        );
        kpiRollups[ft] = { color: colorValue, trend: trendValue, score: scoreValue };
      }

      // Aggregate mission sub-KPIs from child projects so the detail pane
      // can show which outcomes compose the group's aggregate mission score.
      const groupMissionSubkpis: any[] = [];
      for (const item of items) {
        const subkpis = item?.mission_subkpis;
        if (Array.isArray(subkpis) && subkpis.length > 0) {
          const projName = item?.project?.project_name ?? "Unknown";
          const projGuid = item?.project?.project_guid ?? item?.project?.project_GUID ?? "";
          for (const sk of subkpis) {
            groupMissionSubkpis.push({ ...sk, _project_name: projName, _project_guid: projGuid });
          }
        }
      }

      const groupPayload: any = {
        __type: "group",
        id: nodeId,
        groupLabel: label,
        group_path: keyPath,
        depth,
        filterText: searchText,
        group_count: items.length,
        group_items: items,
        // Detail pane: show immediate children if present, else leaf projects
        items: subRows?.length
          ? subRows.map((r: any) => r?.__payload ?? r)
          : items,
        // GroupProgressPage needs subRows for drill-down into subgroups.
        subRows: subRows || [],
        // Aggregated mission sub-KPIs from all child projects (tagged with project name/guid).
        group_mission_subkpis: groupMissionSubkpis,
        project: {
          project_name: `${label} (${items.length})`,
        },
      };

      for (const ft of KPI_FIELDS) {
        // Most KPIs store as `${field}_indicator_*` (e.g., summary_indicator_color).
        groupPayload[`${ft}_indicator_color`] = kpiRollups[ft].color;
        groupPayload[`${ft}_indicator_trend`] = kpiRollups[ft].trend;
        groupPayload[`${ft}_score_0_100`] = kpiRollups[ft].score;

        // Data Reliability KPI uses different keys in the project records & UI.
        if (ft === FIELD_TYPES.DATA) {
          groupPayload["data_reliability_color"] = kpiRollups[ft].color;
          groupPayload["data_reliability_trend"] = kpiRollups[ft].trend;
          groupPayload["data_reliability_score_0_100"] = kpiRollups[ft].score;
        }
      }

      const rowObj: any = {
        id: nodeId,
        __type: "group",
        __payload: groupPayload,
        groupLabel: label,
        depth,
        displayLabel: `${label} (${items.length})`,
        project: {
          project_name: `${label} (${items.length})`,
        },
        __items: items,
        subRows: subRows || [],
      };

      for (const ft of KPI_FIELDS) {
        rowObj[`${ft}_indicator_color`] = kpiRollups[ft].color;
        rowObj[`${ft}_indicator_trend`] = kpiRollups[ft].trend;
        rowObj[`${ft}_score_0_100`] = kpiRollups[ft].score;

        // Data Reliability KPI uses different keys in the project records & UI.
        if (ft === FIELD_TYPES.DATA) {
          rowObj["data_reliability_color"] = kpiRollups[ft].color;
          rowObj["data_reliability_trend"] = kpiRollups[ft].trend;
          rowObj["data_reliability_score_0_100"] = kpiRollups[ft].score;
        }
      }

      return rowObj as any;
    },
    [computeGroupKpiValuesFromItems, searchText],
  );

  const buildHierarchyData = useCallback(
    (rows: any[], groupBy: string): TableRowData[] => {
      const leaves: TableRowData[] = rows.map((it: any, idx: number) => ({
        ...(it as any),
        id: it?.id ?? it?.project?.id ?? `${idx}`,
        __type: "project",
        __flatIndex: idx,
      }));

      if (!groupBy || groupBy === "none") return leaves;

      // DEV-920 Layer 2 migration shim. The legacy hardcoded value
      // "rotary_structure" used to be the dispatch key for the 3-tier
      // rotary tree builder. After the refactor, every hierarchy is
      // dispatched via "hier:<hierarchyGuid>". Existing user sessions
      // have "rotary_structure" persisted in localStorage; without this
      // shim, those sessions would fall into the default flat-group
      // branch and bucket every project under a single "Unspecified"
      // node (because `getGroupValue` looks for it.project.rotary_structure,
      // which is undefined). Treat the stale value as ungrouped instead;
      // the user picks a fresh option from the now-data-driven dropdown.
      // Safe to remove once Skip's session (and any others) have re-saved.
      if (groupBy === "rotary_structure") return leaves;

      /** Normalise a raw geography value: treat null, undefined, empty string,
       *  and the literal "null" (from `String(null)`) as blank. */
      const normGeo = (v: any): string => {
        if (v == null) return "";
        const s = String(v).trim();
        return s.toLowerCase() === "null" ? "" : s;
      };

      const getCommunityParts = (it: any) => {
        const p = it?.project ?? it;

        const rawL1 = normGeo(p?.community_l1 ?? p?.Community_L1 ?? p?.communityL1);
        const rawL2 = normGeo(p?.community_l2 ?? p?.Community_L2 ?? p?.communityL2);
        const rawL3 = normGeo(p?.community_l3 ?? p?.Community_L3 ?? p?.communityL3);

        if (rawL1 || rawL2 || rawL3) {
          return {
            l1: rawL1 || t('grid.geoGlobal'),
            l2: rawL2,   // empty string = no state/province
            l3: rawL3 || normGeo(p?.Community ?? p?.community),  // empty = no city
          };
        }

        // Back-compat: a single community string may contain a full path,
        // e.g. "United States, Massachusetts, Salem".
        const raw = normGeo(p?.Community ?? p?.community);
        if (!raw)
          return { l1: t('grid.geoGlobal'), l2: "", l3: "" };

        const parts = raw
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);

        if (parts.length >= 3) {
          return { l1: parts[0], l2: parts[1], l3: parts.slice(2).join(", ") };
        }
        if (parts.length === 2) {
          return { l1: parts[0], l2: parts[1], l3: "" };
        }
        return { l1: parts[0], l2: "", l3: "" };
      };

      const getGroupValue = (it: any) => {
        if (String(groupBy || "").startsWith("custom:")) {
          const key = String(groupBy).slice("custom:".length);
          const v = it?.custom?.[key];
          return v ?? "Unspecified";
        }
        const p = it?.project ?? it;
        return p[groupBy] ?? "Unspecified";
      };

      if (groupBy === "community") {
        const NATIONAL = t('grid.geoNational');
        const STATE    = t('grid.geoState');

        /** Sort project leaf items alphabetically and tag them. */
        const toProjectLeaves = (items: any[], pathPrefix: string) =>
          items
            .slice()
            .sort((a, b) =>
              String(a?.project?.project_name ?? "").localeCompare(
                String(b?.project?.project_name ?? ""),
              ),
            )
            .map((it, idx) => ({
              ...(it as any),
              id: it?.id ?? it?.project?.id ?? `${pathPrefix}-${idx}`,
              __type: "project",
            }));

        const byL1 = new Map<string, any[]>();
        rows.forEach((it) => {
          const { l1 } = getCommunityParts(it);
          const arr = byL1.get(l1) || [];
          arr.push(it);
          byL1.set(l1, arr);
        });

        return Array.from(byL1.entries())
          .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
          .map(([l1, itemsL1]) => {
            // Split items by whether they have an l2 (state/province)
            const byL2 = new Map<string, any[]>();
            const directL1: any[] = []; // items with no l2 — national scope
            itemsL1.forEach((it) => {
              const { l2 } = getCommunityParts(it);
              if (!l2) {
                directL1.push(it);
              } else {
                const arr = byL2.get(l2) || [];
                arr.push(it);
                byL2.set(l2, arr);
              }
            });

            // Build L2 sub-groups (only for items that have a state/province)
            const l2Nodes: TableRowData[] = Array.from(byL2.entries())
              .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
              .map(([l2, itemsL2]) => {
                // Split items by whether they have an l3 (city)
                const byL3 = new Map<string, any[]>();
                const directL2: any[] = []; // items with no l3 — state scope
                itemsL2.forEach((it) => {
                  const { l3 } = getCommunityParts(it);
                  if (!l3) {
                    directL2.push(it);
                  } else {
                    const arr = byL3.get(l3) || [];
                    arr.push(it);
                    byL3.set(l3, arr);
                  }
                });

                // Build L3 sub-groups (only for items that have a city)
                const l3Nodes: TableRowData[] = Array.from(byL3.entries())
                  .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
                  .map(([l3, itemsL3]) => {
                    const projLeaves = toProjectLeaves(itemsL3, `${l1}-${l2}-${l3}`);
                    return buildGroupNode(
                      l3,
                      [groupBy, l1, l2, l3],
                      itemsL3,
                      projLeaves,
                    );
                  });

                // State-scoped projects (no city) → wrap in "State Projects" sub-group
                const l2Children: TableRowData[] = [...l3Nodes];
                if (directL2.length > 0) {
                  const stateLeaves = toProjectLeaves(directL2, `${l1}-${l2}-state`);
                  l2Children.unshift(
                    buildGroupNode(STATE, [groupBy, l1, l2, STATE], directL2, stateLeaves),
                  );
                }
                return buildGroupNode(l2, [groupBy, l1, l2], itemsL2, l2Children);
              });

            // National-scoped projects (no state) → wrap in "National Projects" sub-group
            const l1Children: TableRowData[] = [...l2Nodes];
            if (directL1.length > 0) {
              const nationalLeaves = toProjectLeaves(directL1, `${l1}-national`);
              l1Children.unshift(
                buildGroupNode(NATIONAL, [groupBy, l1, NATIONAL], directL1, nationalLeaves),
              );
            }
            return buildGroupNode(l1, [groupBy, l1], itemsL1, l1Children);
          });
      }
      // DEV-920 Layer 2. Generic N-tier hierarchy walker. Replaces the
      // hardcoded 3-tier zone/district/club rotary block. Dispatched on
      // any groupBy of the form "hier:<hierarchyGuid>", where the
      // hierarchy is looked up in effectiveHierarchies and its
      // levelOrder (array of field GUIDs, root → leaf) drives the walk.
      //
      // Behavior preserved from the prior rotary block:
      //   - Per-tier "{TierLabel}-wide Projects" leaf for items that
      //     have a parent-tier value but no value for the current tier
      //     (rendered before assigned children).
      //   - Top-tier "{Country} Projects" fallback for items missing a
      //     value at the root tier — preserves the existing UX flourish
      //     where unzoned Rotary projects bucket by their geography
      //     country instead of an undifferentiated "Unspecified" bucket.
      //   - Alphabetical sort at every level. At the root, the country-
      //     fallback bucket(s) sort alongside assigned values.
      //   - Project leaves sorted by project_name within each leaf
      //     bucket.
      //
      // Behavior changed: the walker is now N-tier (any level_order
      // length up to schema_hierarchies.MAX_TIERS = 6), not just 3.
      // The 4-tier "Rotary + Interact" hierarchy renders correctly here.
      if (groupBy.startsWith("hier:")) {
        const hierarchyGuid = groupBy.slice("hier:".length);
        const hier = (effectiveHierarchies || []).find(
          (h: any) =>
            String(h.hierarchyGuid || "").toLowerCase() ===
            hierarchyGuid.toLowerCase(),
        );
        if (!hier) {
          // Hierarchy was deleted, not yet loaded, or unknown — fall
          // back to flat list so the user isn't stranded with a blank
          // grid. The dropdown will refresh on next API tick and the
          // selection becomes valid again, or the user picks another.
          return leaves;
        }

        let levelOrder: string[] = [];
        try {
          const parsed = JSON.parse(hier.levelOrder);
          if (Array.isArray(parsed)) levelOrder = parsed.map(String);
        } catch {
          return leaves;
        }
        if (levelOrder.length === 0) return leaves;

        // Resolve each tier field GUID → { fieldKey, label } once.
        // fieldKey is what we read from `it.custom`; label is the
        // human-readable name used for "{TierLabel}-wide Projects".
        const fieldByGuid = new Map<string, any>();
        for (const f of effectiveSchemaFields || []) {
          fieldByGuid.set(String(f.field_guid || "").toLowerCase(), f);
        }
        const tiers = levelOrder.map((guid) => {
          const f = fieldByGuid.get(String(guid).toLowerCase());
          return {
            fieldGuid: guid,
            fieldKey: f?.field_key ? String(f.field_key) : "",
            label: f?.label ? String(f.label) : "Tier",
          };
        });

        const isMissing = (v: any) =>
          v === null ||
          v === undefined ||
          (typeof v === "string" && v.trim() === "") ||
          String(v).toLowerCase() === "unspecified";

        const readTier = (it: any, fieldKey: string): string | null => {
          if (!fieldKey) return null;
          const v = it?.custom?.[fieldKey];
          return isMissing(v) ? null : String(v);
        };

        // DEV-920 Layer 2 follow-up. The Step 3 picker stores tier values
        // as entity_guid (UUID strings); legacy data may store entity codes
        // or names. The walker buckets by raw value (stable, distinct) but
        // displays via resolveTierLabel. For GUIDs, look up the entity's
        // name (preferred) or code (fallback). For non-GUID values, the
        // lookup misses and we display the raw value as-is — this is what
        // makes legacy "Zone 32" / "District 7780" still display correctly
        // even though the same hierarchy's Club / Interact tiers store GUIDs.
        //
        // The lookup map combines BOTH entity sources because the platform
        // currently bifurcates them: legacy entities (un-prefixed types
        // like rotary_club) live in custom_entities; newer entities created
        // via Sub-Communities (org_-prefixed types like org_rotary_club,
        // org_rotary_interact_club) live in community_entities. Without
        // the second source, hierarchies that mix old and new tier data
        // (e.g., Skip's 4-tier Rotary+Interact where Zone/District are
        // legacy but Club/Interact were just created via Sub-Communities)
        // render half names + half raw GUIDs.
        const tierLabelByGuid = new Map<string, string>();
        const ingestEntity = (e: any) => {
          if (!e?.entity_guid) return;
          const upper = String(e.entity_guid).toUpperCase();
          const label = String(e?.name || e?.entity_code || "").trim();
          if (label && !tierLabelByGuid.has(upper)) {
            tierLabelByGuid.set(upper, label);
          }
        };
        for (const e of effectiveCustomEntities || []) ingestEntity(e);
        for (const e of effectiveCommunityEntities || []) ingestEntity(e);
        const resolveTierLabel = (rawValue: string): string => {
          if (!rawValue) return rawValue;
          return tierLabelByGuid.get(String(rawValue).toUpperCase()) || rawValue;
        };

        const projectLeaves = (
          items: any[],
          pathSoFar: string[],
        ): TableRowData[] =>
          items
            .slice()
            .sort((a, b) =>
              String(a?.project?.project_name ?? "").localeCompare(
                String(b?.project?.project_name ?? ""),
              ),
            )
            .map((it, idx) => ({
              ...(it as any),
              id:
                it?.id ??
                it?.project?.id ??
                `${pathSoFar.join("-")}-${idx}`,
              __type: "project",
            }));

        // Recursive walker. tierIndex = index into `tiers` of the tier
        // we're currently partitioning at. pathSoFar = breadcrumb path
        // for buildGroupNode keying.
        const walk = (
          items: any[],
          tierIndex: number,
          pathSoFar: string[],
        ): TableRowData[] => {
          if (tierIndex >= tiers.length) return projectLeaves(items, pathSoFar);

          const tier = tiers[tierIndex];

          if (tierIndex === 0) {
            // Root tier: fold "missing top tier" items into a country-
            // fallback bucket (or a single "Unspecified {Tier0Label}"
            // bucket when geography is also missing) and let those
            // buckets sort alphabetically alongside assigned values —
            // matching the prior rotary UX.
            const buckets = new Map<string, any[]>();
            items.forEach((it) => {
              const v = readTier(it, tier.fieldKey);
              if (v) {
                const arr = buckets.get(v) || [];
                arr.push(it);
                buckets.set(v, arr);
                return;
              }
              const { l1: country } = getCommunityParts(it);
              const fallbackKey = country
                ? `${country} Projects`
                : `Unspecified ${tier.label}`;
              const arr = buckets.get(fallbackKey) || [];
              arr.push(it);
              buckets.set(fallbackKey, arr);
            });

            // Sort by DISPLAY label (resolved name when key is a GUID),
            // not by raw key — otherwise rows show alphabetically by UUID
            // and look randomly ordered.
            return Array.from(buckets.entries())
              .sort((a, b) =>
                resolveTierLabel(a[0]).localeCompare(resolveTierLabel(b[0])),
              )
              .map(([key, bucketItems]) => {
                // Fallback buckets (no top-tier value for any item)
                // collapse straight to project leaves; assigned buckets
                // recurse to the next tier.
                const isFallbackBucket = !bucketItems.some(
                  (it) => !!readTier(it, tier.fieldKey),
                );
                const displayLabel = resolveTierLabel(key);
                const subRows = isFallbackBucket
                  ? projectLeaves(bucketItems, [...pathSoFar, key])
                  : walk(bucketItems, tierIndex + 1, [...pathSoFar, key]);
                return buildGroupNode(
                  displayLabel,
                  [...pathSoFar, key],
                  bucketItems,
                  subRows,
                );
              });
          }

          // Non-root tier: split assigned vs direct (parent-tier known,
          // current-tier missing).
          //
          // DEV-1135 / Task #222. Ragged-hierarchy fix. Projects missing
          // the current tier value attach as DIRECT LEAVES of the parent
          // group — not wrapped in a synthetic "{TierLabel}-wide Projects"
          // folder. Per Skip's UX call (pattern A): a parent community
          // that owns projects directly AND has subcommunities should
          // show subcommunity folders alongside its direct projects, not
          // force the direct projects into a phantom child folder.
          //
          // Previous behavior bucketed `wide` items under a label like
          // "Interact-wide Projects" beneath every Club, which produced a
          // phantom tier on any project whose tier-N value happened to be
          // NULL. The phantom tier is gone — direct-leaf projects now sit
          // as siblings of the subcommunity folders at the same level.
          //
          // Ordering: subcommunity folders FIRST (alphabetical by display
          // label), direct project leaves SECOND. This keeps the
          // subcommunity structure scannable at the top of each parent
          // group even when the parent owns many direct projects.
          const assigned: any[] = [];
          const direct: any[] = [];
          items.forEach((it) => {
            const v = readTier(it, tier.fieldKey);
            if (v) assigned.push(it);
            else direct.push(it);
          });

          const out: TableRowData[] = [];

          // 1) Subcommunity folders first, sorted by display label.
          const byValue = new Map<string, any[]>();
          assigned.forEach((it) => {
            const v = readTier(it, tier.fieldKey)!;
            const arr = byValue.get(v) || [];
            arr.push(it);
            byValue.set(v, arr);
          });

          Array.from(byValue.entries())
            .sort((a, b) =>
              resolveTierLabel(a[0]).localeCompare(resolveTierLabel(b[0])),
            )
            .forEach(([key, bucketItems]) => {
              const displayLabel = resolveTierLabel(key);
              const subRows = walk(bucketItems, tierIndex + 1, [
                ...pathSoFar,
                key,
              ]);
              out.push(
                buildGroupNode(
                  displayLabel,
                  [...pathSoFar, key],
                  bucketItems,
                  subRows,
                ),
              );
            });

          // 2) Direct project leaves (items with no value at this tier).
          //    Rendered as siblings of the subcommunity folders above —
          //    no phantom "{TierLabel}-wide" wrapper folder.
          if (direct.length > 0) {
            for (const leaf of projectLeaves(direct, pathSoFar)) {
              out.push(leaf);
            }
          }

          return out;
        };

        return walk(rows, 0, [groupBy]);
      }

      const byKey = new Map<string, any[]>();
      rows.forEach((it) => {
        const v = String(getGroupValue(it));
        const arr = byKey.get(v) || [];
        arr.push(it);
        byKey.set(v, arr);
      });

      return Array.from(byKey.entries())
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([label, items]) => {
          const projLeaves = items
            .slice()
            .sort((a, b) =>
              String(a?.project?.project_name ?? "").localeCompare(
                String(b?.project?.project_name ?? ""),
              ),
            )
            .map((it, idx) => ({
              ...(it as any),
              id: it?.id ?? it?.project?.id ?? `${label}-${idx}`,
              __type: "project",
            }));
          return buildGroupNode(label, [groupBy, label], items, projLeaves);
        });
    },
    // DEV-920 Layer 2: the new hier:<guid> branch reads from
    // effectiveHierarchies (to look up levelOrder + presentation) and
    // effectiveSchemaFields (to map field GUIDs → field_key/label for
    // tier resolution). Both must be in deps so the tree rebuilds when
    // the API responds with hierarchies/fields after first render.
    //
    // DEV-971: effectiveCommunityEntities + effectiveCommunityRelationships
    // are inputs to the Geography branch (getCommunityParts reads L1/L2/L3
    // that are client-derived against this tree). Without these deps, the
    // first render captures the entity tree as empty and projects show
    // only country until the user toggles Group By. With them in deps,
    // the callback recomputes when the tree lands and Geography renders
    // its full hierarchy on first load — no user workaround needed.
    [
      buildGroupNode,
      t,
      effectiveHierarchies,
      effectiveSchemaFields,
      effectiveCommunityEntities,
      effectiveCommunityRelationships,
    ],
  );

  const tableData: TableRowData[] = useMemo(() => {
    // IMPORTANT: For KPI rollups we need the same row-shape the rest of the app expects
    // (computed KPI keys live at the row root, not nested).
    return buildHierarchyData(filteredProjects, groupByField);
  }, [buildHierarchyData, filteredProjects, groupByField]);

  // Task #224. Snapshot-diff drives animation. Runs after tableData
  // recomputes; compares each project leaf's prior `{ sortIndex, color/
  // trend per KPI }` to its new value and fires markRowUpdated only for
  // rows that genuinely changed. See the type+ref declaration upstream
  // for the architectural rationale.
  useEffect(() => {
    const flatLeaves: any[] = [];
    const walk = (rows: any[]) => {
      for (const r of rows || []) {
        if (r?.__type === "project") {
          flatLeaves.push(r);
        } else if (Array.isArray(r?.subRows)) {
          walk(r.subRows);
        }
      }
    };
    walk(tableData);

    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const snapshotFromRow = (r: any, sortIndex: number): KpiSnapshot => ({
      sortIndex,
      schedule: {
        color: num(r?.schedule_indicator_color),
        trend: num(r?.schedule_indicator_trend),
      },
      finance: {
        color: num(r?.finance_indicator_color),
        trend: num(r?.finance_indicator_trend),
      },
      mission: {
        color: num(r?.mission_indicator_color),
        trend: num(r?.mission_indicator_trend),
      },
      data: {
        color: num(r?.data_reliability_color),
        trend: num(r?.data_reliability_trend),
      },
      summary: {
        color: num(r?.summary_indicator_color),
        trend: num(r?.summary_indicator_trend),
      },
    });

    const newSnap: Record<string, KpiSnapshot> = {};
    flatLeaves.forEach((r, idx) => {
      const guid = String(r?.project_guid || "").toUpperCase();
      if (!guid) return;
      newSnap[guid] = snapshotFromRow(r, idx);
    });

    const prior = priorSnapshotRef.current;

    // First render: capture and exit — no baseline to compare against.
    if (prior === null) {
      priorSnapshotRef.current = newSnap;
      return;
    }

    const FIELDS = ["schedule", "finance", "mission", "data", "summary"] as const;

    for (const guid of Object.keys(newSnap)) {
      const n = newSnap[guid];
      const p = prior[guid];
      // Brand-new row in this render — capture into snapshot for next
      // diff, but no animation on first sight.
      if (!p) continue;

      const changedFields: string[] = [];
      for (const f of FIELDS) {
        if (n[f].color !== p[f].color || n[f].trend !== p[f].trend) {
          changedFields.push(f);
        }
      }
      const rankChanged = n.sortIndex !== p.sortIndex;

      if (changedFields.length > 0) {
        // Cells whose values genuinely changed get the cell-pulse stamp,
        // AND the whole row flashes. markRowUpdated auto-includes
        // summary when a sub-KPI changes.
        markRowUpdated(guid, changedFields);
      } else if (rankChanged) {
        // Row moved (likely because a grouping field changed) but no
        // KPI value diff. Flash the row only — don't pulse individual
        // cells. Inline rather than calling markRowUpdated so we don't
        // stamp _kpiChangedAtStore for unchanged values.
        const now = Date.now();
        setRecentlyUpdated((prev) => ({ ...prev, [guid]: now }));
        window.setTimeout(() => {
          setRecentlyUpdated((prev) => {
            const next = { ...prev };
            delete next[guid];
            return next;
          });
        }, 2500);
      }
    }

    priorSnapshotRef.current = newSnap;
  }, [tableData, markRowUpdated]);

  // NOTE: With TanStack v8 we render KPI cells directly in the column defs.

  const columns = useMemo(() => {
    const groupFirstSort = (rowA: any, rowB: any, columnId: string) => {
      const aIsGroup = rowA.original?.__type === "group";
      const bIsGroup = rowB.original?.__type === "group";
      if (aIsGroup !== bIsGroup) return aIsGroup ? -1 : 1;
      const a = String(rowA.getValue(columnId) ?? "");
      const b = String(rowB.getValue(columnId) ?? "");
      return a.localeCompare(b);
    };

    return [
      {
        id: "project_name",
        // Move the search input into the table header (where "PROJECTS (#)" used to be).
        header: () => (
          <div className={styles.projectsHeaderWrapper}>
            <div className={styles.projectsHeaderLabel}>
              PROJECTS ({filteredCount})
            </div>
          </div>
        ),
        accessorFn: (row: any) => row?.project?.project_name ?? "",
        sortingFn: groupFirstSort,
        cell: (ctx: any) => {
          const row = ctx.row;
          const original = row.original as any;
          const isGroupRow = original?.__type === "group";
          const depth = row.depth || 0;
          const paddingLeft = 4 + depth * 12;

          const label = original?.project?.project_name || "-";

          const toggle = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            row.toggleExpanded();
          };

          const open = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            if (isGroupRow) {
              onSelect?.(original.__payload ?? original, 0);
              setActiveRowInd(null);
              row.toggleExpanded();
              return;
            }
            // Pass -1 so ProjectDetailPanel opens on the Core tab
            // (not the Work → Performance → Summary sub-tab).
            onSelect?.(original, -1);
          };

          return (
            <div
              className={
                isGroupRow
                  ? styles["group-header-cell"]
                  : styles["project-cell"]
              }
              style={{ paddingLeft }}
            >
              {row.getCanExpand() ? (
                <button
                  type="button"
                  className={`${styles.expander} ${row.getIsExpanded() ? styles.expanded : ""}`}
                  onClick={toggle}
                  aria-label={row.getIsExpanded() ? t('grid.collapse') : t('grid.expand')}
                >
                  <CaretRightIcon className={styles.expanderIcon} />
                </button>
              ) : (
                // Leaf rows: keep indentation aligned with group rows, but render a true placeholder
                // so we don't accidentally inherit any icon/affordance styling.
                <span
                  className={styles.expanderPlaceholder}
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                className={styles["group-header-label"]}
                onClick={open}
              >
                {label}
              </button>
            </div>
          );
        },
      },
      {
        id: "kpi_summary",
        header: () => <SummaryIcon color="var(--text-primary)" />,
        enableSorting: true,
        accessorFn: (row: any) =>
          getKpiSortValue(row as any, FIELD_TYPES.SUMMARY),
        cell: (ctx: any) => {
          const original = ctx.row.original as any;
          const rowId = original?.id;
          const isGroupRow = original?.__type === "group";

          // IMPORTANT: mission/data must match focus by either react-table row id OR project GUID
          const rowGuid = !isGroupRow ? getProjectGuidForRow(ctx.row) : "";

          const isFocusedRowForKpi = isGroupRow
            ? (focusedGroupIds.has(rowId) || focusedGroupId === rowId)
            : focusedRowIds.has(rowId) ||
              (!!rowGuid && focusedRowIds.has(rowGuid));
          const isFocusedCellForKpi =
            isFocusedRowForKpi && focusedKpiField === FIELD_TYPES.SUMMARY;
          const icon = isGroupRow
            ? computeGroupKpiIconFromItems(
                FIELD_TYPES.SUMMARY,
                original.__items || [],
              )
            : getKpiIndicator(FIELD_TYPES.SUMMARY, original, "sm", shouldPulseCell(rowGuid, "summary"));
          return (
            <div
              className={[
                styles.kpiCell,
                isFocusedCellForKpi ? styles.kpiCellFocused : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className={styles.kpiButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroupRow) {
                    // Selecting a group KPI focuses/highlights the group row + its project rows
                    const { groupIds, leafIds } = collectDescendantIds(ctx.row);
                    const sameFocus =
                      focusedGroupId === rowId &&
                      focusedKpiField === FIELD_TYPES.SUMMARY;
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(rowId);
                      setFocusedGroupIds(groupIds);
                      setFocusedKpiField(FIELD_TYPES.SUMMARY);
                      setFocusedRowIds(leafIds);
                      suppressExternalSyncRef.current = true;
                    }
                    onSelect?.(original.__payload ?? original, 0);
                  } else {
                    // Clicking a project KPI focuses/highlights just that KPI cell (and keeps normal row selection)
                    const sameFocus =
                      !focusedGroupId &&
                      focusedKpiField === FIELD_TYPES.SUMMARY &&
                      focusedRowIds.size === 1 &&
                      ((!!rowGuid && focusedRowIds.has(rowGuid)) ||
                        focusedRowIds.has(rowId));
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(null);
                      setFocusedKpiField(FIELD_TYPES.SUMMARY);
                      setFocusedRowIds(new Set(rowGuid ? [rowGuid] : rowId ? [rowId] : []));
                    }
                    onSelect?.(original, 0);
                  }
                }}
              >
                {icon}
              </button>
            </div>
          );
        },
      },
      {
        id: "kpi_schedule",
        header: () => <ScheduleIcon color="var(--text-primary)" />,
        enableSorting: true,
        accessorFn: (row: any) =>
          getKpiSortValue(row as any, FIELD_TYPES.SCHEDULE),
        cell: (ctx: any) => {
          const original = ctx.row.original as any;
          const isGroupRow = original?.__type === "group";

          const rowId = original?.id;

          // IMPORTANT: allow focus to match by either react-table row id OR project GUID
          const rowGuid = !isGroupRow ? getProjectGuidForRow(ctx.row) : "";

          const isFocusedRowForKpi = isGroupRow
            ? (focusedGroupIds.has(rowId) || focusedGroupId === rowId)
            : focusedRowIds.has(rowId) ||
              (!!rowGuid && focusedRowIds.has(rowGuid));
          const isFocusedCellForKpi =
            isFocusedRowForKpi && focusedKpiField === FIELD_TYPES.SCHEDULE;
          const icon = isGroupRow
            ? computeGroupKpiIconFromItems(
                FIELD_TYPES.SCHEDULE,
                original.__items || [],
              )
            : getKpiIndicator(FIELD_TYPES.SCHEDULE, original, "sm", shouldPulseCell(rowGuid, "schedule"));
          return (
            <div
              className={[
                styles.kpiCell,
                isFocusedCellForKpi ? styles.kpiCellFocused : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className={styles.kpiButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroupRow) {
                    // Selecting a group KPI focuses/highlights the group row + its project rows
                    const { groupIds, leafIds } = collectDescendantIds(ctx.row);
                    const sameFocus =
                      focusedGroupId === rowId &&
                      focusedKpiField === FIELD_TYPES.SCHEDULE;
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(rowId);
                      setFocusedGroupIds(groupIds);
                      setFocusedKpiField(FIELD_TYPES.SCHEDULE);
                      setFocusedRowIds(leafIds);
                      suppressExternalSyncRef.current = true;
                    }
                    onSelect?.(original.__payload ?? original, 1);
                  } else {
                    const sameFocus =
                      !focusedGroupId &&
                      focusedKpiField === FIELD_TYPES.SCHEDULE &&
                      focusedRowIds.size === 1 &&
                      (focusedRowIds.has(rowId) ||
                        (!!rowGuid && focusedRowIds.has(rowGuid)));
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(null);
                      setFocusedKpiField(FIELD_TYPES.SCHEDULE);
                      setFocusedRowIds(new Set(rowGuid ? [rowGuid] : rowId ? [rowId] : []));
                    }
                    onSelect?.(original, 1);
                  }
                }}
              >
                {icon}
              </button>
            </div>
          );
        },
      },
      {
        id: "kpi_finance",
        header: () => <CurrencyIcon color="var(--text-primary)" />,
        enableSorting: true,
        accessorFn: (row: any) =>
          getKpiSortValue(row as any, FIELD_TYPES.FINANCE),
        cell: (ctx: any) => {
          const original = ctx.row.original as any;
          const isGroupRow = original?.__type === "group";

          const rowId = original?.id;
          const rowGuid = !isGroupRow ? getProjectGuidForRow(ctx.row) : "";
          const isFocusedRowForKpi = isGroupRow
            ? (focusedGroupIds.has(rowId) || focusedGroupId === rowId)
            : focusedRowIds.has(rowId) ||
              (!!rowGuid && focusedRowIds.has(rowGuid));
          const isFocusedCellForKpi =
            isFocusedRowForKpi && focusedKpiField === FIELD_TYPES.FINANCE;
          const icon = isGroupRow
            ? computeGroupKpiIconFromItems(
                FIELD_TYPES.FINANCE,
                original.__items || [],
              )
            : getKpiIndicator(FIELD_TYPES.FINANCE, original, "sm", shouldPulseCell(rowGuid, "finance"));
          return (
            <div
              className={[
                styles.kpiCell,
                isFocusedCellForKpi ? styles.kpiCellFocused : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className={styles.kpiButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroupRow) {
                    // Selecting a group KPI focuses/highlights the group row + its project rows
                    const { groupIds, leafIds } = collectDescendantIds(ctx.row);
                    const sameFocus =
                      focusedGroupId === rowId &&
                      focusedKpiField === FIELD_TYPES.FINANCE;
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(rowId);
                      setFocusedGroupIds(groupIds);
                      setFocusedKpiField(FIELD_TYPES.FINANCE);
                      setFocusedRowIds(leafIds);
                      suppressExternalSyncRef.current = true;
                    }
                    onSelect?.(original.__payload ?? original, 2);
                  } else {
                    const sameFocus =
                      !focusedGroupId &&
                      focusedKpiField === FIELD_TYPES.FINANCE &&
                      focusedRowIds.size === 1 &&
                      (focusedRowIds.has(rowId) ||
                        (!!rowGuid && focusedRowIds.has(rowGuid)));
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(null);
                      setFocusedKpiField(FIELD_TYPES.FINANCE);
                      // Store the react-table row id when we have it; otherwise keep the guid.
                      // Matching logic supports both.
                      setFocusedRowIds(
                        new Set(rowGuid ? [rowGuid] : rowId ? [rowId] : []),
                      );
                    }
                    onSelect?.(original, 2);
                  }
                }}
              >
                {icon}
              </button>
            </div>
          );
        },
      },
      {
        id: "kpi_mission",
        header: () => <MissionIcon color="var(--text-primary)" />,
        enableSorting: true,
        accessorFn: (row: any) =>
          getKpiSortValue(row as any, FIELD_TYPES.MISSION),
        cell: (ctx: any) => {
          const original = ctx.row.original as any;
          const isGroupRow = original?.__type === "group";

          const rowId = original?.id;
          // IMPORTANT: allow focus to match by either react-table row id OR project GUID
          const rowGuid = !isGroupRow ? getProjectGuidForRow(ctx.row) : "";

          const isFocusedRowForKpi = isGroupRow
            ? (focusedGroupIds.has(rowId) || focusedGroupId === rowId)
            : focusedRowIds.has(rowId) || (!!rowGuid && focusedRowIds.has(rowGuid));

          const isFocusedCellForKpi =
            isFocusedRowForKpi && focusedKpiField === FIELD_TYPES.MISSION;
          const icon = isGroupRow
            ? computeGroupKpiIconFromItems(
                FIELD_TYPES.MISSION,
                original.__items || [],
              )
            : getKpiIndicator(FIELD_TYPES.MISSION, original, "sm", shouldPulseCell(rowGuid, "mission"));
          return (
            <div
              className={[
                styles.kpiCell,
                isFocusedCellForKpi ? styles.kpiCellFocused : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className={styles.kpiButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroupRow) {
                    // Selecting a group KPI focuses/highlights the group row + its project rows
                    const { groupIds, leafIds } = collectDescendantIds(ctx.row);
                    const sameFocus =
                      focusedGroupId === rowId &&
                      focusedKpiField === FIELD_TYPES.MISSION;
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(rowId);
                      setFocusedGroupIds(groupIds);
                      setFocusedKpiField(FIELD_TYPES.MISSION);
                      setFocusedRowIds(leafIds);
                      suppressExternalSyncRef.current = true;
                    }
                    onSelect?.(original.__payload ?? original, 3);
                  } else {
                    const sameFocus =
                      !focusedGroupId &&
                      focusedKpiField === FIELD_TYPES.MISSION &&
                      focusedRowIds.size === 1 &&
                      (focusedRowIds.has(rowId) || (!!rowGuid && focusedRowIds.has(rowGuid)));
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(null);
                      setFocusedKpiField(FIELD_TYPES.MISSION);
                      setFocusedRowIds(new Set(rowGuid ? [rowGuid] : rowId ? [rowId] : []));
                    }
                    onSelect?.(original, 3);
                  }
                }}
              >
                {icon}
              </button>
            </div>
          );
        },
      },
      {
        id: "kpi_data",
        header: () => <DataReliabilityIcon color="var(--text-primary)" />,
        enableSorting: true,
        accessorFn: (row: any) => getKpiSortValue(row as any, FIELD_TYPES.DATA),
        cell: (ctx: any) => {
          const original = ctx.row.original as any;
          const isGroupRow = original?.__type === "group";

          const rowId = original?.id;

          // IMPORTANT: allow focus to match by either react-table row id OR project GUID
          const rowGuid = !isGroupRow ? getProjectGuidForRow(ctx.row) : "";

          const isFocusedRowForKpi = isGroupRow
            ? (focusedGroupIds.has(rowId) || focusedGroupId === rowId)
            : focusedRowIds.has(rowId) ||
              (!!rowGuid && focusedRowIds.has(rowGuid));
          const isFocusedCellForKpi =
            isFocusedRowForKpi && focusedKpiField === FIELD_TYPES.DATA;
          const icon = isGroupRow
            ? computeGroupKpiIconFromItems(
                FIELD_TYPES.DATA,
                original.__items || [],
              )
            : getKpiIndicator(FIELD_TYPES.DATA, original, "sm", shouldPulseCell(rowGuid, "data"));
          return (
            <div
              className={[
                styles.kpiCell,
                isFocusedCellForKpi ? styles.kpiCellFocused : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className={styles.kpiButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroupRow) {
                    // Selecting a group KPI focuses/highlights the group row + its project rows
                    const { groupIds, leafIds } = collectDescendantIds(ctx.row);
                    const sameFocus =
                      focusedGroupId === rowId &&
                      focusedKpiField === FIELD_TYPES.DATA;
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(rowId);
                      setFocusedGroupIds(groupIds);
                      setFocusedKpiField(FIELD_TYPES.DATA);
                      setFocusedRowIds(leafIds);
                      suppressExternalSyncRef.current = true;
                    }
                    onSelect?.(original.__payload ?? original, 4);
                  } else {
                    const sameFocus =
                      !focusedGroupId &&
                      focusedKpiField === FIELD_TYPES.DATA &&
                      focusedRowIds.size === 1 &&
                      (focusedRowIds.has(rowId) ||
                        (!!rowGuid && focusedRowIds.has(rowGuid)));
                    if (sameFocus) {
                      clearFocus();
                    } else {
                      setFocusedGroupId(null);
                      setFocusedKpiField(FIELD_TYPES.DATA);
                      setFocusedRowIds(new Set(rowGuid ? [rowGuid] : rowId ? [rowId] : []));
                    }
                    onSelect?.(original, 4);
                  }
                }}
              >
                {icon}
              </button>
            </div>
          );
        },
      },
    ];
    // NOTE: Intentionally omit `searchText` from deps so the columns array remains stable
    // while typing (prevents header remount + focus loss). `setSearchText` is stable.
  }, [
    computeGroupKpiIconFromItems,
    getKpiSortValue,
    onSelect,
    focusedGroupId,
    focusedKpiField,
    focusedRowIds,
    focusedGroupIds,
    clearFocus,
    collectDescendantIds,
    computeGroupKpiValuesFromItems,
    getKpiValueKeys,
    aggMethod,
    filteredCount,
    shouldPulseCell,
    recentlyUpdated,
    pulseTick,
  ]);

  return (
    <div className={styles.root}>
      <div
        className={styles.scroll}
        style={{ ["--kpi-toolbar-h" as any]: `${toolbarHeight}px` }}
      >
        <div className={styles.toolbar} ref={toolbarRef}>
          <div className={styles.toolbarLeft}>
            {debugMode ? (
              <div style={{ marginLeft: 10, fontSize: 12, opacity: 0.85 }}>
                Sync: extField={String(externalFocusKpiField || "")} extRow=
                {String(externalFocusRowId || "")} | focusedField=
                {String(focusedKpiField || "")} focusedRows={focusedRowIds.size}
              </div>
            ) : null}
            {/* Rollup method — inline dropdown (replaces former gear menu) */}
            <label className={styles.toolbarLabel}>
              {t('grid.rollup')}
              <select
                className={styles["groupby-select"]}
                value={aggMethod}
                onChange={(e) => setAggMethod(e.target.value as any)}
                aria-label={t('grid.rollup')}
              >
                <option value="average">{t('grid.useAverage')}</option>
                <option value="best">{t('grid.useBest')}</option>
                <option value="worst">{t('grid.useWorst')}</option>
              </select>
            </label>
            {/*
              DEV-966 follow-up. Show/hide completed projects pill —
              moved to right edge of toolbar via flex spacer below
              (DEV-972 follow-up V5 design). The pill aesthetic
              matches the dropdowns visually; the accent dot
              indicates active/inactive state at a glance.
            */}
            {/* TODO: Live/Official KPI mode pill — commented out until logic is fixed.
            {typeof kpiMode === "string" &&
              typeof onKpiModeChange === "function" && (
                <div
                  className={styles.kpiModePill}
                  role="radiogroup"
                  aria-label="KPI mode"
                  title="Live uses current (draft) KPI values. Official uses published values."
                >
                  <button
                    type="button"
                    className={`${styles.kpiModePillBtn} ${kpiMode === "live" ? styles.kpiModePillActive : ""}`}
                    onClick={() => onKpiModeChange("live")}
                    aria-pressed={kpiMode === "live"}
                  >
                    Live
                  </button>
                  <button
                    type="button"
                    className={`${styles.kpiModePillBtn} ${kpiMode === "official" ? styles.kpiModePillActive : ""}`}
                    onClick={() => onKpiModeChange("official")}
                    aria-pressed={kpiMode === "official"}
                  >
                    Official
                  </button>
                </div>
              )}
            */}
            {!hideCollection && (
            <label className={styles.toolbarLabel}>
              Collection
              <select
                className={styles["collection-select"]}
                value={selectedCollectionGuid}
                onChange={(e) => {
                  setSelectedCollectionGuid(e.target.value);
                  setGroupByField("");
                  setActiveRowInd(null);
                  clearFocus();
                  onSelect?.(null, 0);
                }}
                aria-label="Select collection"
              >
                {collectionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            )}
            {/* TODO: View selector — disabled until saved-views feature is stable. */}
            {false && (
            <label className={styles.toolbarLabel}>
              View
              <select
                className={styles["view-select"] || styles["groupby-select"]}
                value={selectedViewId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedViewId(v);
                  if (v && v !== "__NONE__") applySavedView(v);
                }}
                aria-label="Select saved view"
              >
                <option value="__NONE__">None</option>
                {savedViews.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.name}
                  </option>
                ))}
              </select>
            </label>
            )}
            {/*
              DEV-972 V5: removed the inline borderInlineStart visual
              separators between Group by + Changes. The pill-shaped
              dropdowns (via .groupby-select CSS) handle separation
              naturally; explicit pipe lines added visual noise.
            */}
            <label className={styles.toolbarLabel}>
              Group by
              <select
                className={styles["groupby-select"]}
                value={groupByField}
                onChange={(e) => onGroupByChange(e.target.value)}
                aria-label="Group projects by"
              >
                {groupOptions.hierarchy.length > 0 && (
                  <optgroup label="Hierarchies">
                    {groupOptions.hierarchy.map((opt) => (
                      <option key={opt.value || "none"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                )}

                <optgroup label="Standard Fields">
                  {groupOptions.standard.map((opt) => (
                    <option key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>

                {effectiveSelectedCollectionGuid !== "__ALL__" &&
                  groupOptions.collection.length > 0 && (
                    <optgroup label="Collection Fields">
                      {groupOptions.collection.map((opt) => (
                        <option key={opt.value || "none"} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
              </select>
            </label>
            <label className={styles.toolbarLabel}>
              {t('grid.changesLabel')}
              <select
                className={styles["groupby-select"]}
                value={changeHighlight}
                onChange={(e) => setChangeHighlight(e.target.value as ChangeHighlightWindow)}
                aria-label={t('grid.changesLabel')}
              >
                {CHANGE_HIGHLIGHT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            {!hideSearch && (
              <div className={styles.searchWrapper}>
                <input
                  ref={searchInputRef}
                  className={styles.searchInput}
                  type="search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t('grid.searchProjects')}
                  aria-label={t('grid.searchProjects')}
                />
              </div>
            )}{" "}
            {/*
              DEV-972 V5 design. Show-completed pill at the right
              edge of the toolbar, separated from the aggregation /
              view controls by a flex spacer. The accent dot signals
              state (on = blue glow, off = muted gray) without
              needing an explicit ✓ / ✗ icon. Clicking toggles.
              Persistence is handled by the showCompleted state hook
              alongside the rest of the view state in LS_VIEW_KEY.
            */}
            <div className={styles.toolbarSpacer} aria-hidden="true" />
            <button
              type="button"
              className={`${styles.toolbarPillToggle} ${showCompleted ? styles.on : ''}`}
              onClick={() => setShowCompleted(!showCompleted)}
              aria-pressed={showCompleted}
              aria-label={showCompleted
                ? 'Showing completed projects · click to hide'
                : 'Hiding completed projects · click to show'}
              title={showCompleted
                ? 'Showing completed projects · click to hide'
                : 'Hiding completed projects · click to show'}
            >
              <span className={styles.toolbarPillToggleDot} aria-hidden="true" />
              <span>Show completed</span>
            </button>
            {/* Settings menu (impacts group-row KPI aggregation) */}
          </div>
        </div>

        <Table
          tableClassName={styles.kpiFixedTable}
          persistKey={`kpiGrid::${effectiveSelectedCollectionGuid}::${groupByField}`}
          columns={columns as any}
          data={tableData as any}
          onRowClick={onRowClickHandler}
          getRowClassName={(r: any) => {
            const isActive =
              r?.__type === "project" &&
              typeof r.__flatIndex === "number" &&
              r.__flatIndex === activeRowInd;

            const projGuid = String(
              r?.project_guid ??
                r?.project?.project_GUID ??
                r?.project?.project_guid ??
                "",
            );

            const isFocused =
              // The clicked group itself
              (focusedGroupId && r?.id === focusedGroupId) ||
              // Subgroups within a focused group-of-groups
              (r?.__type === "group" && r?.id && focusedGroupIds.has(r.id)) ||
              // Projects within a focused group (only when focusedRowIds is populated)
              (r?.__type === "project" &&
                focusedRowIds.size > 0 &&
                ((r?.id && focusedRowIds.has(r.id)) ||
                  (!!projGuid && focusedRowIds.has(projGuid))));
            const isUpdated = !!(
              projGuid && (recentlyUpdated as any)[projGuid]
            );

            const isSelectedProject =
              !!selectedProjectGuid &&
              r?.__type === "project" &&
              projGuid === String(selectedProjectGuid);

            const cls = [
              isActive ? "active-row" : "",
              isSelectedProject ? styles.rowSelected : "",
              isFocused ? styles.focusRow : "",
              isUpdated ? styles.rowUpdated : "",
            ]
              .filter(Boolean)
              .join(" ");

            return cls || undefined;
          }}
          focusRowId={externalFocusRowId || null}
        />
      </div>
    </div>
  );
};

export default KpiGridTable;
